/**
 * RecipeList Component
 *
 * Displays a list of recipes in a grid layout with search functionality.
 * Filters recipes in real-time based on the search query.
 */

import { useState, useEffect, useMemo } from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useRecipeStore } from '../../stores/recipeStore';
import { RecipeCard } from './RecipeCard';
import './recipes.css';

export function RecipeList() {
  const { t } = useTranslation('recipes');
  const { recipes, fetchRecipes, isLoading, error } = useRecipeStore();
  const [searchQuery, setSearchQuery] = useState('');

  useEffect(() => {
    fetchRecipes();
  }, [fetchRecipes]);

  // Filter recipes based on search query
  const filteredRecipes = useMemo(() => {
    if (!searchQuery.trim()) {
      return recipes;
    }

    const query = searchQuery.toLowerCase();
    return recipes.filter((recipe) => {
      const titleMatch = recipe.title.toLowerCase().includes(query);
      const descriptionMatch = recipe.description?.toLowerCase().includes(query) || false;
      const ingredientsMatch = recipe.ingredients?.some((ingredient) =>
        ingredient.name.toLowerCase().includes(query)
      ) || false;

      return titleMatch || descriptionMatch || ingredientsMatch;
    });
  }, [recipes, searchQuery]);

  if (error) {
    return (
      <div className="recipe-container">
        <div className="error-message" role="alert">
          {error}
        </div>
      </div>
    );
  }

  return (
    <div className="recipe-container">
      <div className="recipe-header">
        <h1>{t('myRecipes')}</h1>
        <p className="recipe-subtitle">
          {t('searchSubtitle')}
        </p>
        <div style={{ display: 'flex', gap: '0.5rem', flexWrap: 'wrap', marginTop: '0.5rem' }}>
          <Link to="/recipes/new" className="btn-primary">{t('createRecipe')}</Link>
          <Link to="/recipes/import-pdf" className="btn-secondary">{t('importFromPdf')}</Link>
        </div>
      </div>

      <div className="recipe-search-section">
        <div className="search-input-wrapper">
          <input
            type="text"
            className="search-input"
            placeholder={t('searchPlaceholder')}
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            disabled={isLoading}
            aria-label="Search recipes"
          />
          {searchQuery && (
            <button
              className="clear-search-button"
              onClick={() => setSearchQuery('')}
              aria-label="Clear search"
            >
              ✕
            </button>
          )}
        </div>
      </div>

      {isLoading ? (
        <div className="loading-state" role="status" aria-live="polite">
          <p>{t('loadingRecipes')}</p>
        </div>
      ) : filteredRecipes.length === 0 ? (
        <div className="empty-state" role="status" aria-live="polite">
          {searchQuery ? (
            <>
              <p>{t('noRecipesFound', { query: searchQuery })}</p>
              <button
                className="btn-secondary"
                onClick={() => setSearchQuery('')}
              >
                {t('clearSearch')}
              </button>
            </>
          ) : (
            <>
              <p>{t('noRecipesYet')}</p>
              <div style={{ display: 'flex', gap: '0.5rem', flexWrap: 'wrap', justifyContent: 'center' }}>
                <Link to="/recipes/new" className="btn-primary">
                  {t('createRecipe')}
                </Link>
                <Link to="/recipes/import-pdf" className="btn-secondary">
                  {t('importFromPdf')}
                </Link>
              </div>
            </>
          )}
        </div>
      ) : (
        <>
          <div className="results-count" role="status" aria-live="polite" aria-atomic="true">
            {t('showingRecipes', { count: filteredRecipes.length })}
          </div>
          <div className="recipe-grid" role="list" aria-label={t('recipeList')}>
            {filteredRecipes.map((recipe) => (
              <RecipeCard key={recipe.id} recipe={recipe} />
            ))}
          </div>
        </>
      )}
    </div>
  );
}

export default RecipeList;
